import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    BookOutlined,
    SunOutlined,
    MoonOutlined,
    DesktopOutlined,
    SlidersOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LoadingOutlined,
    FileTextOutlined,
    ReloadOutlined,
    DownOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { THEME_MODES, THEME_STORAGE_KEY } from "./devGuideData";
import useDevGuideData from "../Utils/hooks/useDevGuideData";
import DevGuideMarkdown from "./DevGuideMarkdown";
import { ReactComponent as AmnsLogo } from "./amnsLogo.svg";
import "./devguide.css";

/** Icon per appearance option, matching the Light / Dark / Auto row. */
const THEME_ICONS = {
    light: <SunOutlined />,
    dark: <MoonOutlined />,
    auto: <DesktopOutlined />,
};

/**
 * Resolve the appearance choice to a concrete theme. "auto" follows the OS
 * preference via prefers-color-scheme; "light"/"dark" are used as-is.
 *
 * @param {string} mode - "light" | "dark" | "auto".
 * @returns {"light"|"dark"}
 */
const resolveTheme = (mode) => {
    if (mode === "auto") {
        const prefersDark =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-color-scheme: dark)").matches;
        return prefersDark ? "dark" : "light";
    }
    return mode;
};

/** Same slug rule DevGuideMarkdown uses, so TOC ids match heading ids. */
const slugify = (text) =>
    String(text ?? "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");

/**
 * Derive the "On this page" anchors from a document's raw Markdown by scanning
 * for `##` / `###` headings. Skips fenced code blocks so `#` lines inside code
 * aren't mistaken for headings.
 *
 * @param {string} content - Raw Markdown.
 * @returns {Array<{id: string, text: string, level: number}>}
 */
const extractAnchors = (content) => {
    if (!content) return [];
    const anchors = [];
    let inFence = false;
    // Normalize CRLF/CR to LF and strip a leading BOM so heading detection is
    // reliable regardless of how the Markdown was authored/stored.
    const normalized = content.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
    for (const rawLine of normalized.split("\n")) {
        const line = rawLine.trimEnd();
        if (/^\s*```/.test(line)) {
            inFence = !inFence;
            continue;
        }
        if (inFence) continue;
        // Match ##/### headings (allow a little leading whitespace).
        const m = /^\s*(#{2,3})\s+(.*)$/.exec(line);
        if (m) {
            const text = m[2].replace(/[#*`]/g, "").trim();
            if (!text) continue;
            anchors.push({ id: slugify(text), text, level: m[1].length });
        }
    }
    return anchors;
};

/**
 * Dev Guide screen — a read-only Markdown documentation reader with light/dark
 * theming, backed by the S3 Lambda (sections = folders, documents = Markdown).
 *
 * Three-column layout:
 *   • Left: section/document navigation loaded from the backend.
 *   • Center: the selected document's rendered Markdown, with loading/error states.
 *   • Right: an "on this page" list (from the doc's headings) and the
 *     Light / Dark / Auto appearance toggle that drives the theme.
 */
const DevGuide = () => {
    const { listSections, listDocuments, getDocument } = useDevGuideData();

    // ---- Navigation data ----
    const [sections, setSections] = useState([]);
    const [sectionsLoading, setSectionsLoading] = useState(true);
    const [sectionsError, setSectionsError] = useState("");
    // Documents per section id, lazily loaded when a section is first opened.
    const [docsBySection, setDocsBySection] = useState({});
    // Which section groups are expanded in the left nav.
    const [expanded, setExpanded] = useState({});

    // ---- Active document ----
    const [activeKey, setActiveKey] = useState(null);
    const [activeTitle, setActiveTitle] = useState("");
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [docContent, setDocContent] = useState("");
    const [docMeta, setDocMeta] = useState({ updated: "" });
    const [docLoading, setDocLoading] = useState(false);
    const [docError, setDocError] = useState("");

    // In-memory cache of fetched documents, keyed by S3 key. Re-opening a
    // previously viewed document renders instantly from here instead of
    // re-hitting the Lambda. Lives in a ref so it persists across renders
    // without causing re-renders when written.
    const docCache = useRef(new Map());

    // ---- UI chrome ----
    const [leftCollapsed, setLeftCollapsed] = useState(false);
    const [themeMode, setThemeMode] = useState(
        () => sessionStorage.getItem(THEME_STORAGE_KEY) || "light"
    );
    const [comfortableWidth, setComfortableWidth] = useState(true);

    const resolvedTheme = resolveTheme(themeMode);

    // Persist the appearance choice so it survives navigation.
    useEffect(() => {
        sessionStorage.setItem(THEME_STORAGE_KEY, themeMode);
    }, [themeMode]);

    // When on "auto", re-render if the OS theme flips while the page is open.
    useEffect(() => {
        if (themeMode !== "auto" || !window.matchMedia) return undefined;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => setThemeMode("auto"); // triggers a resolve
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, [themeMode]);

    // Load the documents for a section (once), returning the list.
    const loadDocuments = useCallback(
        async (sectionId) => {
            if (docsBySection[sectionId]) return docsBySection[sectionId];
            const docs = await listDocuments(sectionId);
            setDocsBySection((prev) => ({ ...prev, [sectionId]: docs }));
            return docs;
        },
        [docsBySection, listDocuments]
    );

    // Open a document: render from cache if present, else fetch and cache it.
    // Pass { force: true } to bypass the cache (used by the error retry).
    const openDocument = useCallback(
        async (sectionId, document, { force = false } = {}) => {
            setActiveKey(document.key);
            setActiveSectionId(sectionId);
            setActiveTitle(document.title || document.id || "");

            // Cache hit: render immediately, no network call.
            const cached = docCache.current.get(document.key);
            if (cached && !force) {
                setActiveTitle(cached.title || document.title || document.id || "");
                setDocMeta({ updated: cached.updated });
                setDocContent(cached.content);
                setDocError("");
                setDocLoading(false);
                return;
            }

            setDocLoading(true);
            setDocError("");
            try {
                const { title, updated, content } = await getDocument(document.key);
                const resolved = {
                    title: title || document.title || document.id || "",
                    updated,
                    content,
                };
                docCache.current.set(document.key, resolved);
                setActiveTitle(resolved.title);
                setDocMeta({ updated: resolved.updated });
                setDocContent(resolved.content);
            } catch (err) {
                setDocError(err.message || "Failed to load document");
                setDocContent("");
            } finally {
                setDocLoading(false);
            }
        },
        [getDocument]
    );

    // Toggle a section group open/closed; load its docs on first open.
    const toggleSection = useCallback(
        async (sectionId) => {
            const willOpen = !expanded[sectionId];
            setExpanded((prev) => ({ ...prev, [sectionId]: willOpen }));
            if (willOpen) {
                try {
                    await loadDocuments(sectionId);
                } catch {
                    // Per-section load failures surface as an empty group; the
                    // top-level error banner covers total failures.
                }
            }
        },
        [expanded, loadDocuments]
    );

    // Initial load: sections, then auto-open the first and its first document.
    const loadSections = useCallback(async () => {
        setSectionsLoading(true);
        setSectionsError("");
        try {
            const result = await listSections();
            setSections(result);
            if (result.length) {
                const first = result[0];
                setExpanded({ [first.id]: true });
                const docs = await loadDocuments(first.id);
                if (docs.length) openDocument(first.id, docs[0]);
            }
        } catch (err) {
            setSectionsError(err.message || "Failed to load guidelines");
        } finally {
            setSectionsLoading(false);
        }
        // loadDocuments/openDocument are stable enough for an initial load; we
        // intentionally run this once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listSections]);

    useEffect(() => {
        loadSections();
    }, [loadSections]);

    // "On this page" anchors from the active document's headings.
    const anchors = useMemo(() => extractAnchors(docContent), [docContent]);

    // TEMP DEBUG: inspect what content arrived and which anchors were parsed.
    // Remove once the "On this page" list is confirmed working.
    useEffect(() => {
        if (!docContent) return;
        // eslint-disable-next-line no-console
        console.log("[DevGuide] content length:", docContent.length);
        // eslint-disable-next-line no-console
        console.log("[DevGuide] first 300 chars:", JSON.stringify(docContent.slice(0, 300)));
        // eslint-disable-next-line no-console
        console.log("[DevGuide] extracted anchors:", anchors);
    }, [docContent, anchors]);

    return (
        <div
            className={`dg-wrapper${leftCollapsed ? " is-left-collapsed" : ""}`}
            data-theme={resolvedTheme}
        >
            {/* ---------- Sticky top bar: logo + main title ---------- */}
            <header className="dg-topbanner">
                <AmnsLogo className="dg-brand-logo" role="img" aria-label="AMNS India logo" />
                <span className="dg-topbanner-divider" aria-hidden="true" />
                <h1 className="dg-topbanner-title">GenAI Developer Guidelines</h1>
            </header>

            {/* ---------- Body: three columns below the top bar ---------- */}
            <div className="dg-body">
                {/* ---------- Left sidebar (collapsible) ---------- */}
                <aside className="dg-left">
                    <button
                        type="button"
                        className="dg-collapse-btn"
                        aria-label={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-expanded={!leftCollapsed}
                        onClick={() => setLeftCollapsed((v) => !v)}
                    >
                        {leftCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </button>

                    <div className="dg-left-only dg-left-body">
                        {sectionsLoading && (
                            <div className="dg-nav-status">
                                <LoadingOutlined spin /> Loading…
                            </div>
                        )}

                        {!sectionsLoading && sectionsError && (
                            <div className="dg-nav-status dg-nav-error">
                                <span>{sectionsError}</span>
                                <button type="button" className="dg-retry" onClick={loadSections}>
                                    <ReloadOutlined /> Retry
                                </button>
                            </div>
                        )}

                        {!sectionsLoading && !sectionsError && !sections.length && (
                            <div className="dg-nav-status">No guidelines found.</div>
                        )}

                        {!sectionsLoading && !sectionsError && !!sections.length && (
                            <nav className="dg-nav">
                                {sections.map((section) => {
                                    const isOpen = !!expanded[section.id];
                                    const docs = docsBySection[section.id];
                                    return (
                                        <div key={section.id} className="dg-nav-group">
                                            <button
                                                type="button"
                                                className="dg-nav-section"
                                                aria-expanded={isOpen}
                                                onClick={() => toggleSection(section.id)}
                                            >
                                                {isOpen ? <DownOutlined /> : <RightOutlined />}
                                                <span>{section.label}</span>
                                            </button>

                                            {isOpen && (
                                                <div className="dg-nav-docs">
                                                    {!docs && (
                                                        <div className="dg-nav-status dg-nav-status-sm">
                                                            <LoadingOutlined spin /> Loading…
                                                        </div>
                                                    )}
                                                    {docs && !docs.length && (
                                                        <div className="dg-nav-status dg-nav-status-sm">
                                                            No documents.
                                                        </div>
                                                    )}
                                                    {docs &&
                                                        docs.map((d) => (
                                                            <button
                                                                key={d.key || d.id}
                                                                type="button"
                                                                className={`dg-nav-item${
                                                                    activeKey === d.key ? " is-active" : ""
                                                                }`}
                                                                onClick={() => openDocument(section.id, d)}
                                                            >
                                                                <FileTextOutlined className="dg-nav-doc-icon" />
                                                                <span className="dg-nav-label">
                                                                    {d.title || d.id}
                                                                </span>
                                                                {activeKey === d.key && (
                                                                    <span className="dg-nav-dot" />
                                                                )}
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </nav>
                        )}
                    </div>
                </aside>

                {/* ---------- Center content ---------- */}
                <main className="dg-center">
                    <div className="dg-topbar">
                        <div className="dg-breadcrumb">
                            <BookOutlined />
                            <span>/</span>
                            <span>
                                {sections.find((s) => s.id === activeSectionId)?.label || "Guidelines"}
                            </span>
                            {activeTitle && (
                                <>
                                    <span>›</span>
                                    <span className="dg-breadcrumb-current">{activeTitle}</span>
                                </>
                            )}
                        </div>
                    </div>

                    <article
                        key={activeKey || "empty"}
                        className={`dg-article${comfortableWidth ? " is-comfortable" : ""}`}
                    >
                        {docLoading && (
                            <div className="dg-doc-status">
                                <LoadingOutlined spin /> Loading document…
                            </div>
                        )}

                        {!docLoading && docError && (
                            <div className="dg-doc-status dg-doc-error">
                                <span>{docError}</span>
                                {activeKey && activeSectionId && (
                                    <button
                                        type="button"
                                        className="dg-retry"
                                        onClick={() =>
                                            openDocument(
                                                activeSectionId,
                                                { key: activeKey, title: activeTitle },
                                                { force: true }
                                            )
                                        }
                                    >
                                        <ReloadOutlined /> Retry
                                    </button>
                                )}
                            </div>
                        )}

                        {!docLoading && !docError && !activeKey && (
                            <div className="dg-doc-status">
                                Select a document from the left to start reading.
                            </div>
                        )}

                        {!docLoading && !docError && activeKey && (
                            <>
                                <h1 id="top" className="dg-h1">
                                    {activeTitle}
                                </h1>
                                {docMeta.updated && (
                                    <div className="dg-meta">
                                        <span>Updated {docMeta.updated}</span>
                                    </div>
                                )}
                                <hr className="dg-divider" />
                                <DevGuideMarkdown content={docContent} />
                            </>
                        )}
                    </article>
                </main>

                {/* ---------- Right rail ---------- */}
                <aside className="dg-right">
                    <div className="dg-rail-eyebrow">ON THIS PAGE</div>
                    <nav className="dg-toc">
                        {anchors.length === 0 && (
                            <span className="dg-toc-empty">—</span>
                        )}
                        {anchors.map((a) => (
                            <a
                                key={a.id}
                                href={`#${a.id}`}
                                className={`dg-toc-item${a.level === 3 ? " dg-toc-sub" : ""}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    const prefersReduced = window.matchMedia?.(
                                        "(prefers-reduced-motion: reduce)"
                                    ).matches;
                                    document.getElementById(a.id)?.scrollIntoView({
                                        behavior: prefersReduced ? "auto" : "smooth",
                                        block: "start",
                                    });
                                }}
                            >
                                {a.text}
                            </a>
                        ))}
                    </nav>

                    <div className="dg-prefs">
                        <div className="dg-prefs-head">
                            <span>Reading preferences</span>
                            <SlidersOutlined />
                        </div>

                        <div className="dg-prefs-label">Appearance</div>
                        <div className="dg-appearance" role="group" aria-label="Appearance">
                            {THEME_MODES.map((mode) => (
                                <button
                                    key={mode.key}
                                    type="button"
                                    className={`dg-appearance-btn${
                                        themeMode === mode.key ? " is-active" : ""
                                    }`}
                                    aria-pressed={themeMode === mode.key}
                                    onClick={() => setThemeMode(mode.key)}
                                >
                                    {THEME_ICONS[mode.key]}
                                    <span>{mode.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="dg-switch-row">
                            <span>Comfortable width</span>
                            <button
                                type="button"
                                className={`dg-switch${comfortableWidth ? " is-on" : ""}`}
                                aria-pressed={comfortableWidth}
                                onClick={() => setComfortableWidth((v) => !v)}
                            >
                                <span className="dg-switch-knob" />
                            </button>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default DevGuide;
